import React, { useEffect, useRef, useState } from 'react';
import Avatar from './Avatar';
import { timeAgo } from './communityUtils';
import './community.css';
import {
  fetchNotifications, fetchUnreadNotificationCount, markNotificationRead, markAllNotificationsRead,
  CommunityNotification,
} from '../../../services/communityApi';
import { useCampus } from '../../../components/campus/CampusContext';
import { notificationPlace } from '../../../components/campus/campusNotify';
import { OPEN_BELL_EVENT } from '../../../components/campus/art/BuildingBadges';

function notifText(n: CommunityNotification): string {
  const who = n.actor?.display_name ?? 'Someone';
  if (n.notification_type === 'friend_request') return `${who} sent you a friend request`;
  if (n.notification_type === 'friend_accepted') return `${who} accepted your friend request`;
  if (n.notification_type === 'new_message') return `${who} sent you a message`;
  if (n.notification_type === 'like') return `${who} liked your ${n.source_type}`;
  if (n.notification_type === 'mention') return `${who} mentioned you in a ${n.source_type}`;
  return n.source_type === 'comment' ? `${who} replied to you` : `${who} commented on your post`;
}

/**
 * Topbar notification bell (Community). Polls a lightweight unread count every
 * 45s; opening the panel fetches the list. Clicking an item marks it read and
 * jumps to the community feed. Degrades silently for users with no community
 * membership (count stays 0). Lives in the portal shell so it shows everywhere.
 *
 * Campus (C8): while the campus is active, every item walks through the campus door to its place (campusNotify); with the
 * campus off, the classic behavior below is unchanged.
 *
 * C23 (rule R10): `onDismissed` fires when the panel closes without a pick (the bell again, or a press outside), so the
 * campus home can recenter its map, as it does when the person card, the list or the Me panel closes.
 *
 * C23 phase 3 (WP-10): while the campus is active, a press on a map notification pin (OPEN_BELL_EVENT) opens this panel.
 */
const NotificationBell: React.FC<{ onDismissed?: () => void }> = ({ onDismissed }) => {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<CommunityNotification[] | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const { active: campusActive, enterThroughDoor } = useCampus();

  useEffect(() => {
    const load = () => fetchUnreadNotificationCount().then(setCount).catch(() => {});
    load();
    const t = window.setInterval(load, 45_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); onDismissed?.(); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onDismissed]);

  useEffect(() => {
    if (!campusActive) return undefined;
    const onPin = () => { setOpen(true); fetchNotifications().then(setItems).catch(() => setItems([])); };
    window.addEventListener(OPEN_BELL_EVENT, onPin);
    return () => window.removeEventListener(OPEN_BELL_EVENT, onPin);
  }, [campusActive]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchNotifications().then(setItems).catch(() => setItems([]));
    else onDismissed?.();
  };

  const onItem = (n: CommunityNotification) => {
    if (!n.read) { markNotificationRead(n.id).catch(() => {}); setCount((c) => Math.max(0, c - 1)); }
    setOpen(false);
    if (campusActive) { enterThroughDoor(notificationPlace(n.notification_type).route, null); return; }
    // Friend / DM notifications point at the rail (present on every page) — no jump.
    if (n.source_type === 'friendship' || n.source_type === 'dm') return;
    window.location.assign('/portal/community');
  };

  const onReadAll = async () => {
    await markAllNotificationsRead().catch(() => {});
    setCount(0);
    setItems((prev) => (prev ? prev.map((n) => ({ ...n, read: true })) : prev));
  };

  return (
    <div className="cm-bell" ref={ref}>
      <button type="button" className="cm-bell-btn" onClick={toggle} aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}>
        <svg viewBox="0 0 24 24" fill="none"><path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M13.7 21a2 2 0 0 1-3.4 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
        {count > 0 && <span className="cm-bell-badge">{count > 9 ? '9+' : count}</span>}
      </button>
      {open && (
        <div className="cm-bell-panel" role="dialog" aria-label="Notifications">
          <div className="cm-bell-head">
            <b>Notifications</b>
            {count > 0 && <button type="button" onClick={onReadAll}>Mark all read</button>}
          </div>
          {items === null && <div className="cm-empty">Loading…</div>}
          {items !== null && items.length === 0 && <div className="cm-empty">You’re all caught up.</div>}
          {items?.map((n) => (
            <button type="button" key={n.id} className={`cm-notif${n.read ? '' : ' unread'}`} onClick={() => onItem(n)}>
              <Avatar name={n.actor?.display_name ?? '?'} src={n.actor?.avatar_url ?? null} size="sm" />
              <div className="cm-notif-body">
                <span className="cm-notif-text">{notifText(n)}</span>
                <span className="cm-notif-time">{timeAgo(n.created_at)}</span>
              </div>
              {!n.read && <span className="cm-notif-dot" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
